import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import apiClient from '../../api/client';
import Icon from '../../components/AppIcon';

const AcademicPage = () => {
    const navigate = useNavigate();
    const { academics, setAcademicRecords, setAcademicsLoading, setAcademicsError } = useStore();
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        semester: '',
        gpa: '',
        subjects: [],
    });
    const [subjectInput, setSubjectInput] = useState({ name: '', score: '' });

    // Fetch academic records from database on mount
    useEffect(() => {
        setAcademicsError(null);
        const fetchRecords = async () => {
            try {
                setAcademicsLoading(true);
                const response = await apiClient.get('/academic/records');
                setAcademicRecords(response.data.records || []);
            } catch (error) {
                console.warn('Initial fetch of academic records skipped/failed:', error.message);
                setAcademicsLoading(false);
                // Do not display automatic error banner on initial mount when user hasn't taken any action
            }
        };

        fetchRecords();
    }, []);

    // Save all records to database
    const saveRecordsToDatabase = async (updatedRecordsList) => {
        try {
            setAcademicsLoading(true);
            
            // Map frontend format to backend format
            const recordsToSave = updatedRecordsList.map(record => ({
                semester: parseInt(record.semester) || null,
                gpa: parseFloat(record.gpa) || null,
                details: record.details || (record.subjects && record.subjects.length > 0 ? { subjects: record.subjects } : null)
            }));

            const response = await apiClient.post('/academic/records', { records: recordsToSave });
            
            // Refresh records from server
            const fetchResponse = await apiClient.get('/academic/records');
            setAcademicRecords(fetchResponse.data.records || []);
            setAcademicsLoading(false);
        } catch (error) {
            console.error('Failed to save academic records:', error);
            console.error('Error response:', error.response?.data);
            setAcademicsLoading(false);
            setAcademicsError(error.response?.data?.error || error.message || 'Failed to save academic records');
            throw error;
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                let newRecords = [];
                if (file.name.endsWith('.json')) {
                    const data = JSON.parse(event.target.result);
                    newRecords = data;
                } else if (file.name.endsWith('.csv')) {
                    newRecords = parseCSV(event.target.result);
                }

                // Save to database
                const updatedList = [...academics.records, ...newRecords];
                await saveRecordsToDatabase(updatedList);
                alert('Academic records imported successfully!');
            } catch (error) {
                alert('Failed to import records. Please check the format and try again.');
            }
        };
        reader.readAsText(file);
    };

    const parseCSV = (csvText) => {
        const lines = csvText.split('\n');
        const records = [];

        for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',');
            if (values.length >= 2) {
                records.push({
                    semester: parseInt(values[0]) || i,
                    gpa: parseFloat(values[1]) || 0,
                    subjects: [],
                });
            }
        }
        
        return records;
    };

    const handleAddSubject = () => {
        if (subjectInput.name && subjectInput.score) {
            setFormData({
                ...formData,
                subjects: [...formData.subjects, { ...subjectInput }],
            });
            setSubjectInput({ name: '', score: '' });
        }
    };

    const handleRemoveSubject = (index) => {
        setFormData({
            ...formData,
            subjects: formData.subjects.filter((_, i) => i !== index),
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate inputs
        if (!formData.semester || !formData.gpa) {
            alert('Please fill in both Semester and GPA fields');
            return;
        }

        const semesterNum = parseInt(formData.semester);
        const gpaNum = parseFloat(formData.gpa);

        if (isNaN(semesterNum) || semesterNum < 1) {
            alert('Please enter a valid semester number (1 or greater)');
            return;
        }

        if (isNaN(gpaNum) || gpaNum < 0 || gpaNum > 10) {
            alert('Please enter a valid GPA between 0 and 10');
            return;
        }

        const record = {
            semester: semesterNum,
            gpa: gpaNum,
            subjects: formData.subjects,
        };

        try {
            let updatedRecords;
            if (editingId) {
                // Update existing record
                updatedRecords = academics.records.map((r) =>
                    r.id === editingId ? { ...r, ...record } : r
                );
            } else {
                // Add new record with temporary ID
                updatedRecords = [...academics.records, { ...record, id: Date.now() }];
            }

            // Save to database
            await saveRecordsToDatabase(updatedRecords);

            setFormData({ semester: '', gpa: '', subjects: [] });
            setShowAddForm(false);
            setEditingId(null);
        } catch (error) {
            console.error('Save error:', error);
            alert('Failed to save record. Please try again.');
        }
    };

    const handleEdit = (record) => {
        setFormData({
            semester: record.semester.toString(),
            gpa: record.gpa.toString(),
            subjects: record.additionalInfo?.subjects || record.subjects || [],
        });
        setEditingId(record.id);
        setShowAddForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this record?')) {
            return;
        }

        try {
            setAcademicsLoading(true);
            await apiClient.delete(`/academic/records/${id}`);
            
            // Refresh records from server
            const response = await apiClient.get('/academic/records');
            setAcademicRecords(response.data.records || []);
        } catch (error) {
            console.error('Failed to delete record:', error);
            setAcademicsError(error.message || 'Failed to delete record');
            alert('Failed to delete record. Please try again.');
        }
    };

    // Calculate average GPA from records
    const averageGPA = academics.records.length > 0
        ? (academics.records.reduce((acc, r) => acc + (parseFloat(r.gpa) || 0), 0) / academics.records.length).toFixed(2)
        : '0.00';

    return (
        <div className="min-h-screen">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
                    <div>
                        <h1 className="heading-serif text-display text-[#111111] dark:text-white">
                            Academic Records
                        </h1>
                        <p className="text-[#6B6B6B] dark:text-[#A1A1A1] mt-1">
                            Add your semester-wise academic performance
                        </p>
                    </div>
                    <Button onClick={() => navigate('/coding')}>
                        Next: Coding Profile →
                    </Button>
                </div>

                {/* Error Display */}
                {academics.error && (
                    <div className="card-pastel-pink rounded-2xl p-4 mb-6">
                        <div className="flex items-start">
                            <span className="text-xl mr-3">⚠️</span>
                            <div className="flex-1">
                                <h3 className="text-[#111111] font-semibold">Error</h3>
                                <p className="text-[#6B6B6B] text-sm mt-1">{academics.error}</p>
                            </div>
                            <button 
                                onClick={() => setAcademicsError(null)}
                                className="text-[#6B6B6B] hover:text-[#111111] transition-colors"
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                )}

                {/* Loading Indicator */}
                {academics.loading && (
                    <div className="card-pastel-blue rounded-2xl p-4 mb-6 text-center">
                        <p className="text-[#6B6B6B]">Saving your data...</p>
                    </div>
                )}

                {/* Stats Card */}
                {academics.records.length > 0 && (
                    <div className="bg-white dark:bg-[#1F2023] rounded-2xl border border-[#E8E5DF] dark:border-[rgba(255,255,255,0.08)] shadow-card p-6 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <p className="text-sm text-[#909090]">Total Semesters</p>
                                <p className="text-3xl font-semibold text-[#111111] dark:text-white mt-1">
                                    {academics.records.length}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-[#909090]">Average GPA</p>
                                <p className="text-3xl font-semibold text-[#111111] dark:text-white mt-1">
                                    {averageGPA}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-[#909090]">Trend</p>
                                <p className="text-3xl font-semibold text-[#111111] dark:text-white mt-1">
                                    {academics.records.length >= 2 &&
                                        academics.records[academics.records.length - 1].gpa >
                                        academics.records[academics.records.length - 2].gpa
                                        ? '↗ Improving'
                                        : '→ Stable'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Upload Section */}
                <div className="bg-white dark:bg-[#1F2023] rounded-2xl border border-[#E8E5DF] dark:border-[rgba(255,255,255,0.08)] shadow-card p-6 mb-6">
                    <h2 className="text-xl font-semibold text-[#111111] dark:text-white mb-4">Import Records</h2>
                    <div className="flex flex-col md:flex-row gap-4">
                        <label className="flex-1 cursor-pointer">
                            <input
                                type="file"
                                accept=".json,.csv"
                                onChange={handleFileUpload}
                                className="hidden"
                            />
                            <div className="border-2 border-dashed border-[#E8E5DF] dark:border-[rgba(255,255,255,0.1)] rounded-2xl p-6 text-center hover:border-[#111111]/30 dark:hover:border-white/30 transition-colors">
                                <Icon name="FolderUp" size={36} className="text-[#000000] dark:text-white mx-auto mb-2" />
                                <p className="text-sm font-medium text-[#404040] dark:text-[#A1A1A1]">
                                    Upload CSV or JSON
                                </p>
                                <p className="text-xs text-[#909090] mt-1">
                                    Click to browse files
                                </p>
                            </div>
                        </label>
                        <div className="flex items-center">
                            <span className="text-[#909090]">or</span>
                        </div>
                        <Button
                            onClick={() => setShowAddForm(!showAddForm)}
                            variant="outline"
                            className="flex-1"
                        >
                            ✏️ Add Manually
                        </Button>
                    </div>
                </div>

                {/* Add/Edit Form */}
                {showAddForm && (
                    <div className="bg-white dark:bg-[#1F2023] rounded-2xl border border-[#E8E5DF] dark:border-[rgba(255,255,255,0.08)] shadow-card p-6 mb-6">
                        <h2 className="text-xl font-semibold text-[#111111] dark:text-white mb-4">
                            {editingId ? 'Edit Record' : 'Add New Record'}
                        </h2>
                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <Input
                                    label="Semester"
                                    type="number"
                                    min="1"
                                    max="20"
                                    value={formData.semester}
                                    onChange={(e) => setFormData({ ...formData, semester: e.target.value })}
                                    placeholder="e.g., 1"
                                    required
                                />
                                <Input
                                    label="GPA"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="10"
                                    value={formData.gpa}
                                    onChange={(e) => setFormData({ ...formData, gpa: e.target.value })}
                                    placeholder="e.g., 8.5"
                                    required
                                />
                            </div>

                            {/* Subjects Section */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-[#404040] dark:text-[#A1A1A1] mb-2">Subjects (Optional)</label>
                                <div className="flex gap-2 mb-2">
                                    <Input
                                        placeholder="Subject name"
                                        value={subjectInput.name}
                                        onChange={(e) => setSubjectInput({ ...subjectInput, name: e.target.value })}
                                    />
                                    <Input
                                        type="number"
                                        placeholder="Score"
                                        value={subjectInput.score}
                                        onChange={(e) => setSubjectInput({ ...subjectInput, score: e.target.value })}
                                    />
                                    <Button type="button" onClick={handleAddSubject} variant="outline">
                                        Add
                                    </Button>
                                </div>
                                {formData.subjects.length > 0 && (
                                    <div className="space-y-2">
                                        {formData.subjects.map((subject, index) => (
                                            <div
                                                key={index}
                                                className="flex justify-between items-center bg-[#F8F7F3] dark:bg-[#2a2b2e] p-3 rounded-xl"
                                            >
                                                <span className="text-sm text-[#404040] dark:text-[#A1A1A1]">
                                                    {subject.name}: {subject.score}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveSubject(index)}
                                                    className="text-[#EF4444] hover:text-[#DC2626] text-sm"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3">
                                <Button type="submit">{editingId ? 'Update' : 'Add'} Record</Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setEditingId(null);
                                        setFormData({ semester: '', gpa: '', subjects: [] });
                                    }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Records List */}
                {academics.records.length > 0 ? (
                    <div className="bg-white dark:bg-[#1F2023] rounded-2xl border border-[#E8E5DF] dark:border-[rgba(255,255,255,0.08)] shadow-card overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-[#F8F7F3] dark:bg-[#2a2b2e]">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">
                                        Semester
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">
                                        GPA
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">
                                        Subjects
                                    </th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E8E5DF] dark:divide-[rgba(255,255,255,0.06)]">
                                {academics.records
                                    .sort((a, b) => a.semester - b.semester)
                                    .map((record) => (
                                        <tr key={record.id} className="hover:bg-[#F8F7F3]/50 dark:hover:bg-[#2a2b2e]/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#111111] dark:text-white">
                                                Semester {record.semester}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B6B6B] dark:text-[#A1A1A1]">
                                                {record.gpa}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-[#6B6B6B] dark:text-[#A1A1A1]">
                                                {record.additionalInfo?.subjects?.length || record.subjects?.length || 0} subjects
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                                                <button
                                                    onClick={() => handleEdit(record)}
                                                    className="text-[#111111] hover:text-[#6B6B6B] dark:text-white dark:hover:text-[#A1A1A1] transition-colors"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(record.id)}
                                                    className="text-[#EF4444] hover:text-[#DC2626] transition-colors"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-[#1F2023] rounded-2xl border border-[#E8E5DF] dark:border-[rgba(255,255,255,0.08)] shadow-card p-12 text-center">
                        <Icon name="GraduationCap" size={44} className="text-[#000000] dark:text-white mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-[#111111] dark:text-white mb-2">
                            No Records Yet
                        </h3>
                        <p className="text-[#6B6B6B] dark:text-[#A1A1A1] mb-4">
                            Upload a file or add records manually to get started
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AcademicPage;